import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { NotificationInboxRow } from "@/components/NotificationInboxRow";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Label } from "@/components/ui/Input";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { PageTitleWithHelp, HoverHintContent } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import {
  fetchNotificationsList,
  HUB_NOTIFICATION_TOPIC_LABELS,
  HUB_NOTIFICATION_TOPICS,
  isHubNotificationTopic,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationReadInListRows,
  markNotificationUnread,
  markNotificationUnreadInListRows,
  notificationsListQueryKey,
  notificationsUnreadCountQueryKey,
  parseNotificationListFilter,
  readStoredNotificationFilter,
  removeNotificationFromListResult,
  removeNotificationFromListRows,
  snoozeNotificationInListRows,
  storeNotificationFilter,
  type HubNotificationTopic,
  type NotificationListCursor,
  type NotificationListFilter,
  type NotificationListResult,
  type NotificationListRow,
} from "@/lib/notificationsApi";

const MARK_ALL_READ_FILTERED_HOVER_HINT =
  "This only marks the filtered notifications as read, not all notifications.";

const PAGE_HELP =
  "In-app notifications for support tickets, purchase requests, transfer quantity adjustments, and inventory discrepancies. Unread items stay bold until you mark them read. Use ownership to claim or assign actionable items when you are a topic owner or module administrator.";

const FILTER_OPTIONS: Array<{ value: NotificationListFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "snoozed", label: "Snoozed" },
];

function parseStatusFilter(searchParams: URLSearchParams): NotificationListFilter {
  const filterParam = parseNotificationListFilter(searchParams.get("filter"));
  if (filterParam != null) {
    return filterParam;
  }
  if (searchParams.get("includeSnoozed") === "1") {
    return "snoozed";
  }
  return readStoredNotificationFilter() ?? "unread";
}

function parseTopicFilters(searchParams: URLSearchParams): HubNotificationTopic[] {
  const topicsParam = searchParams.get("topics");
  const legacyTopicParam = searchParams.get("topic");
  const rawValues =
    topicsParam != null && topicsParam.trim() !== ""
      ? topicsParam.split(",")
      : legacyTopicParam != null && legacyTopicParam.trim() !== ""
        ? [legacyTopicParam]
        : [];
  const topics: HubNotificationTopic[] = [];
  for (const raw of rawValues) {
    const trimmed = raw.trim();
    if (trimmed !== "" && isHubNotificationTopic(trimmed) && !topics.includes(trimmed)) {
      topics.push(trimmed);
    }
  }
  return topics;
}

function topicFilterSummary(topics: HubNotificationTopic[]): string {
  if (topics.length === 0) {
    return "notifications";
  }
  if (topics.length === 1) {
    return `${HUB_NOTIFICATION_TOPIC_LABELS[topics[0]!].toLowerCase()} notifications`;
  }
  return "matching notifications";
}

export function NotificationsPage(): JSX.Element {
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = useMemo(() => parseStatusFilter(searchParams), [searchParams]);
  const topicFilters = useMemo(() => parseTopicFilters(searchParams), [searchParams]);

  const listFilters = useMemo(
    () => ({
      filter,
      topics: topicFilters,
    }),
    [filter, topicFilters],
  );

  const [extraRows, setExtraRows] = useState<NotificationListRow[]>([]);
  const [nextCursor, setNextCursor] = useState<NotificationListCursor | null>(null);
  const [loadMoreBusy, setLoadMoreBusy] = useState(false);
  const [markReadEventId, setMarkReadEventId] = useState<string | null>(null);
  const [markUnreadEventId, setMarkUnreadEventId] = useState<string | null>(null);

  function onNotificationSnoozed(
    eventId: string,
    unreadCount: number,
    snoozedUntil: string,
  ): void {
    queryClient.setQueryData(notificationsUnreadCountQueryKey, { unreadCount });
    if (filter === "snoozed") {
      return;
    }
    if (filter === "all") {
      queryClient.setQueryData<NotificationListResult | undefined>(
        notificationsListQueryKey(listFilters),
        (current) => {
          if (current == null) {
            return current;
          }
          return {
            ...current,
            rows: snoozeNotificationInListRows(current.rows, eventId, snoozedUntil),
          };
        },
      );
      setExtraRows((current) => snoozeNotificationInListRows(current, eventId, snoozedUntil));
      return;
    }
    queryClient.setQueryData<NotificationListResult | undefined>(
      notificationsListQueryKey(listFilters),
      (current) => (current != null ? removeNotificationFromListResult(current, eventId) : current),
    );
    setExtraRows((current) => removeNotificationFromListRows(current, eventId));
  }

  const listQuery = useQuery({
    queryKey: notificationsListQueryKey(listFilters),
    queryFn: () =>
      fetchNotificationsList({
        filter: listFilters.filter,
        topics: listFilters.topics,
        limit: 50,
      }),
  });

  useEffect(() => {
    const filterParam = parseNotificationListFilter(searchParams.get("filter"));
    if (filterParam != null) {
      storeNotificationFilter(filterParam);
      return;
    }
    if (searchParams.get("includeSnoozed") === "1") {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("filter", readStoredNotificationFilter() ?? "unread");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("includeSnoozed") !== "1") {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("includeSnoozed");
    if (nextParams.get("filter") == null) {
      nextParams.set("filter", "snoozed");
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setExtraRows([]);
    setNextCursor(null);
  }, [listFilters.filter, listFilters.topics]);

  useEffect(() => {
    if (listQuery.data != null && extraRows.length === 0) {
      setNextCursor(listQuery.data.nextCursor);
    }
  }, [listQuery.data, extraRows.length]);

  const rows = useMemo(() => {
    const first = listQuery.data?.rows ?? [];
    if (extraRows.length === 0) {
      return first;
    }
    const seen = new Set(first.map((row) => row.eventId));
    return [...first, ...extraRows.filter((row) => !seen.has(row.eventId))];
  }, [listQuery.data?.rows, extraRows]);

  const markReadMutation = useMutation({
    mutationFn: (eventId: string) => markNotificationRead(eventId),
    onMutate: (eventId) => {
      setMarkReadEventId(eventId);
    },
    onSuccess: (data, eventId) => {
      queryClient.setQueryData(notificationsUnreadCountQueryKey, data);
      const readAt = new Date().toISOString();

      if (filter === "unread") {
        queryClient.setQueryData<NotificationListResult | undefined>(
          notificationsListQueryKey(listFilters),
          (current) => {
          if (current == null) {
            return current;
          }
          return {
            ...current,
            rows: markNotificationReadInListRows(current.rows, eventId, readAt),
          };
        },
        );
        setExtraRows((current) => markNotificationReadInListRows(current, eventId, readAt));
        return;
      }

      void queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : String(error),
      });
    },
    onSettled: () => {
      setMarkReadEventId(null);
    },
  });

  const markUnreadMutation = useMutation({
    mutationFn: (eventId: string) => markNotificationUnread(eventId),
    onMutate: (eventId) => {
      setMarkUnreadEventId(eventId);
    },
    onSuccess: (data, eventId) => {
      queryClient.setQueryData(notificationsUnreadCountQueryKey, data);
      queryClient.setQueryData<NotificationListResult | undefined>(
        notificationsListQueryKey(listFilters),
        (current) => {
          if (current == null) {
            return current;
          }
          return {
            ...current,
            rows: markNotificationUnreadInListRows(current.rows, eventId),
          };
        },
      );
      setExtraRows((current) => markNotificationUnreadInListRows(current, eventId));
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : String(error),
      });
    },
    onSettled: () => {
      setMarkUnreadEventId(null);
    },
  });

  const markAllMutation = useMutation({
    mutationFn: () =>
      markAllNotificationsRead(topicFilters.length > 0 ? topicFilters : undefined),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationsUnreadCountQueryKey, data);
      void queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
      showToast({ tone: "success", message: "Marked all as read." });
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : String(error),
      });
    },
  });

  function setTopicFilters(nextTopics: HubNotificationTopic[]): void {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("topic");
    if (nextTopics.length === 0) {
      nextParams.delete("topics");
    } else {
      nextParams.set("topics", nextTopics.join(","));
    }
    setSearchParams(nextParams, { replace: true });
  }

  function toggleTopicFilter(topic: HubNotificationTopic): void {
    const nextTopics = topicFilters.includes(topic)
      ? topicFilters.filter((value) => value !== topic)
      : [...topicFilters, topic];
    setTopicFilters(nextTopics);
  }

  function setFilter(next: NotificationListFilter): void {
    storeNotificationFilter(next);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("includeSnoozed");
    nextParams.set("filter", next);
    setSearchParams(nextParams, { replace: true });
  }

  async function onLoadMore(): Promise<void> {
    if (nextCursor == null || loadMoreBusy) {
      return;
    }
    setLoadMoreBusy(true);
    try {
      const page = await fetchNotificationsList({
        filter: listFilters.filter,
        topics: listFilters.topics,
        cursorCreatedAt: nextCursor.createdAt,
        cursorEventId: nextCursor.eventId,
        limit: 50,
      });
      setExtraRows((current) => [...current, ...page.rows]);
      setNextCursor(page.nextCursor);
    } catch (error) {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : String(error),
      });
    } finally {
      setLoadMoreBusy(false);
    }
  }

  useEffect(() => {
    if (listQuery.isError) {
      showToast({
        tone: "error",
        message: listQuery.error instanceof ApiError ? listQuery.error.message : String(listQuery.error),
      });
    }
  }, [listQuery.isError, listQuery.error, showToast]);

  return (
    <div className="flex min-h-0 flex-col gap-4 lg:h-[calc(100vh-10rem)]">
      <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Notifications</PageTitleWithHelp>
        <HoverHintContent hoverHint={MARK_ALL_READ_FILTERED_HOVER_HINT} align="end">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={markAllMutation.isPending || rows.length === 0}
            isLoading={markAllMutation.isPending}
            onClick={() => markAllMutation.mutate()}
          >
            Mark all read
          </Button>
        </HoverHintContent>
      </header>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(16rem,18rem)_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:items-stretch lg:overflow-hidden">
        <Card className="flex h-full min-h-0 flex-col overflow-hidden">
          <CardHeader className="shrink-0">
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardBody className="min-h-0 flex-1 space-y-3 overflow-y-auto">
            <div className="space-y-1">
              <Label>Status</Label>
              <SegmentedControl
                aria-label="Notification status filter"
                value={filter}
                onChange={setFilter}
                options={FILTER_OPTIONS}
              />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <Label>Topics</Label>
                {topicFilters.length > 0 ? (
                  <Button type="button" size="sm" variant="ghost" onClick={() => setTopicFilters([])}>
                    Clear topic filters
                  </Button>
                ) : null}
              </div>
              <div className="flex flex-col gap-2">
                {HUB_NOTIFICATION_TOPICS.map((topic) => {
                  const isOn = topicFilters.includes(topic);
                  return (
                    <Button
                      key={topic}
                      type="button"
                      size="sm"
                      variant={isOn ? "primary" : "secondary"}
                      aria-pressed={isOn}
                      className="h-auto min-h-8 w-full justify-start whitespace-normal py-1.5 text-left"
                      onClick={() => toggleTopicFilter(topic)}
                    >
                      {HUB_NOTIFICATION_TOPIC_LABELS[topic]}
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="flex h-full min-h-0 max-h-[calc(100vh-14rem)] flex-col overflow-hidden lg:max-h-none">
          <CardHeader className="shrink-0">
            <CardTitle>Inbox</CardTitle>
          </CardHeader>
          <CardBody className="min-h-0 flex-1 space-y-2 overflow-y-auto p-0 sm:p-0">
            {listQuery.isLoading ? (
              <p className="px-5 py-4 text-sm text-slate-500">Loading notifications…</p>
            ) : listQuery.isError ? (
              <p className="px-5 py-4 text-sm text-rose-700" role="alert">
                Could not load notifications.
              </p>
            ) : rows.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-slate-600">
                {filter === "unread"
                  ? "No unread notifications."
                  : filter === "snoozed"
                    ? topicFilters.length > 0
                      ? `No snoozed ${topicFilterSummary(topicFilters)}.`
                      : "No snoozed notifications."
                    : topicFilters.length > 0
                      ? `No ${topicFilterSummary(topicFilters)}.`
                      : "No notifications yet."}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <NotificationInboxRow
                    key={row.eventId}
                    row={row}
                    markReadPending={markReadMutation.isPending && markReadEventId === row.eventId}
                    markUnreadPending={markUnreadMutation.isPending && markUnreadEventId === row.eventId}
                    onMarkRead={
                      row.readAt == null
                        ? () => markReadMutation.mutate(row.eventId)
                        : undefined
                    }
                    onMarkUnread={
                      row.readAt != null
                        ? () => markUnreadMutation.mutate(row.eventId)
                        : undefined
                    }
                    onSnoozed={({ eventId, unreadCount, snoozedUntil }) =>
                      onNotificationSnoozed(eventId, unreadCount, snoozedUntil)
                    }
                  />
                ))}
              </ul>
            )}
            {nextCursor != null ? (
              <div className="border-t border-slate-100 px-5 py-3">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={loadMoreBusy}
                  isLoading={loadMoreBusy}
                  onClick={() => {
                    void onLoadMore();
                  }}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
